// ##ExpectedOwnerResolution The practitioner expected to weigh in on a
// proposed change to a resource —
// derived dynamically from current server state at render time (never stored
// on the proposal, so ownership reassignments after the bundle was built are
// always reflected). Distinct from the review task's owner (who fields the
// whole review) and from the decider (who actually recorded a decision):
// this is who each individual change concerns.
//
// Resolution is two-tier because some ownership rules need server lookups:
//   - 'practitioner' — the resource names its own person directly (a task's
//     assignee, an appointment's practitioner participant). Done.
//   - 'pcp' — the change belongs with the participant's primary care
//     provider. Orders (ServiceRequest / MedicationRequest) carry the
//     requester as `candidate`: when that requester is an INTERNAL PCP (a
//     Seen practitioner with the PCP role — e.g. a covering PCP), they own
//     the decision; otherwise (external requester, or internal non-PCP) it
//     falls to the participant's IDT-assigned PCP. Initial/top-level care
//     plans always defer to the IDT PCP; goal and intervention care plans
//     resolve directly to their author instead (see below) and only fall
//     back to this tier when unauthored. The caller resolves the tier-2
//     lookups (practitioner read + IDT search).

import type {
  Appointment,
  CarePlan,
  MedicationRequest,
  Practitioner,
  Reference,
  Resource,
  ServiceRequest,
  Task,
} from '@medplum/fhirtypes'
import { hasCodeableConcept } from '#src/codeableConcept.ts'
import {
  GOAL_CARE_PLAN_CATEGORY,
  INTERVENTION_CARE_PLAN_CATEGORY,
} from '#src/models/care-plans/constants.ts'
import { Role } from '#src/roles.ts'

export type ExpectedOwnerResolution =
  | { kind: 'practitioner'; practitioner: Reference<Practitioner> }
  | { kind: 'pcp'; candidate?: Reference<Practitioner> }

function practitionerReference(
  ref: Reference | undefined
): Reference<Practitioner> | undefined {
  return ref?.reference?.startsWith('Practitioner/')
    ? (ref as Reference<Practitioner>)
    : undefined
}

// The per-resource-type ownership rule table — the single place the
// resource-based branching lives. Only orders and initial care plans lean
// PCP; tasks, appointments, and goal/intervention care plans name their own
// person and never fall back to one (goals/interventions fall back to the
// IDT PCP only when unauthored).
//
//   assignee                 — Task.owner, directly
//   practitioner-participant — the appointment's practitioner actor, directly
//   requesting-pcp           — the requester when they are an internal PCP,
//                              else the participant's IDT-assigned PCP
//   care-plan-author         — a goal/intervention's CarePlan.author when
//                              set, else the participant's IDT-assigned PCP;
//                              an initial/top-level care plan always defers
//                              to the IDT-assigned PCP outright
const EXPECTED_OWNER_RULES = {
  Task: 'assignee',
  Appointment: 'practitioner-participant',
  ServiceRequest: 'requesting-pcp',
  MedicationRequest: 'requesting-pcp',
  CarePlan: 'care-plan-author',
} as const satisfies Partial<Record<Resource['resourceType'], string>>

// Resolve the expected-owner rule for a resource, or undefined when the type
// has no ownership semantics worth surfacing (no rule registered, or a
// direct rule whose person is unset).
export function resourceExpectedOwner(
  resource: Resource
): ExpectedOwnerResolution | undefined {
  const rule =
    EXPECTED_OWNER_RULES[
      resource.resourceType as keyof typeof EXPECTED_OWNER_RULES
    ]
  switch (rule) {
    case 'assignee': {
      const owner = practitionerReference((resource as Task).owner)
      return owner ? { kind: 'practitioner', practitioner: owner } : undefined
    }
    case 'practitioner-participant': {
      // The first practitioner actor — Seen appointments carry exactly one.
      const practitioner = (resource as Appointment).participant
        ?.map((p) => practitionerReference(p.actor))
        .find(Boolean)
      return practitioner ? { kind: 'practitioner', practitioner } : undefined
    }
    case 'requesting-pcp':
      return {
        kind: 'pcp',
        candidate: practitionerReference(
          (resource as ServiceRequest | MedicationRequest).requester
        ),
      }
    case 'care-plan-author': {
      const carePlan = resource as CarePlan
      const isGoalOrIntervention =
        hasCodeableConcept(carePlan.category, GOAL_CARE_PLAN_CATEGORY) ||
        hasCodeableConcept(carePlan.category, INTERVENTION_CARE_PLAN_CATEGORY)
      const author = practitionerReference(carePlan.author)
      return isGoalOrIntervention && author
        ? { kind: 'practitioner', practitioner: author }
        : { kind: 'pcp' }
    }
    default:
      return undefined
  }
}

// The tier-2 lookups a 'pcp'-kind resolution needs, supplied by the caller
// (they require server reads): the loaded candidate practitioner, and the
// participant's IDT-assigned PCP.
export interface ExpectedOwnerLookups {
  // The loaded `candidate` requester — structural (isInternal + roles) so any
  // practitioner wrapper qualifies.
  candidate?: { isInternal: boolean; roles?: Role[] | undefined }
  idtPcp?: Reference<Practitioner>
}

// Final owner resolution: direct rules pass through; the PCP rule prefers the
// requester when they are an internal PCP (covering PCP case), else the
// IDT-assigned PCP.
export function resolveExpectedOwner(
  resolution: ExpectedOwnerResolution | undefined,
  lookups: ExpectedOwnerLookups
): Reference<Practitioner> | undefined {
  if (!resolution) {
    return undefined
  }
  if (resolution.kind === 'practitioner') {
    return resolution.practitioner
  }
  if (
    resolution.candidate &&
    lookups.candidate?.isInternal &&
    lookups.candidate.roles?.includes(Role.PCP)
  ) {
    return resolution.candidate
  }
  return lookups.idtPcp
}
